interface ImageFile {
  id: string
  name: string
  path: string
  category: 'gallery' | 'classes' | 'kits'
  uploadedAt: string
  size?: number
  type?: string
  dataUrl?: string  // Store the actual image data
}

interface AffectedItem {
  id: string
  name: string
  type: 'class' | 'kit' | 'gallery'
}

interface ImageDeletionImpact {
  imageId: string
  canDelete: boolean
  affectedItems: {
    classes: AffectedItem[]
    kits: AffectedItem[]
    galleryItems: AffectedItem[]
  }
  totalAffectedCount: number
}

class ImageManagerService {
  private storageKey = 'salty_mermaid_images'
  private subscribers: Array<(images: ImageFile[]) => void> = []

  // Get all images with JSON fallback
  getAllImages(): ImageFile[] {
    const stored = localStorage.getItem(this.storageKey)
    if (stored) {
      try {
        return JSON.parse(stored)
      } catch (error) {
        console.error('imageManager: Error parsing localStorage images:', error)
      }
    }

    // Auto-sync from JSON file if localStorage is empty
    this.syncFromJsonFile()
    return []
  }

  // Auto-sync images from JSON file to localStorage
  private syncFromJsonFile(): void {
    fetch('/data/images.json')
      .then(response => {
        if (response.ok) {
          return response.json()
        }
        throw new Error('No images.json found')
      })
      .then(jsonImages => {
        console.log('imageManager: Auto-syncing', jsonImages.length, 'images from JSON to localStorage')
        localStorage.setItem(this.storageKey, JSON.stringify(jsonImages))
        this.notifySubscribers()
      })
      .catch(error => {
        console.error('imageManager: Error auto-syncing from JSON:', error)
      })
  }

  // Get images by category
  getImagesByCategory(category: 'gallery' | 'classes' | 'kits'): ImageFile[] {
    return this.getAllImages().filter(img => img.category === category)
  }

  // Upload/Add new image
  async uploadImage(file: File, category: 'gallery' | 'classes' | 'kits'): Promise<ImageFile | null> {
    try {
      // Validate file
      if (!this.validateImageFile(file)) {
        throw new Error('Invalid image file. Please use JPG, PNG, or WebP under 5MB.')
      }

      // Generate unique ID and filename
      const id = this.generateId()
      const fileExtension = file.name.split('.').pop()?.toLowerCase()
      const fileName = `${id}.${fileExtension}`
      const filePath = `/images/${category}/${fileName}`

      // Convert file to data URL for frontend storage
      const dataUrl = await this.fileToDataUrl(file)
      
      const imageFile: ImageFile = {
        id,
        name: file.name,
        path: filePath,
        category,
        uploadedAt: new Date().toISOString(),
        size: file.size,
        type: file.type,
        dataUrl: dataUrl  // Store the actual image data
      }

      // Store image metadata and data
      const images = this.getAllImages()
      images.push(imageFile)
      localStorage.setItem(this.storageKey, JSON.stringify(images))
      
      console.log(`Uploaded ${file.name} as ${fileName} in ${category} category`)
      
      this.notifySubscribers()
      return imageFile
    } catch (error) {
      console.error('Failed to upload image:', error)
      return null
    }
  }

  // Check what items would be affected if image is deleted
  checkImageDeletionImpact(imageId: string): ImageDeletionImpact {
    const impact: ImageDeletionImpact = {
      imageId,
      canDelete: true,
      affectedItems: {
        classes: [],
        kits: [],
        galleryItems: []
      },
      totalAffectedCount: 0
    }

    try {
      // Check classes
      const classesData = localStorage.getItem('salty_mermaid_classes')
      if (classesData) {
        const classes = JSON.parse(classesData)
        const affectedClasses = classes.filter((cls: any) => cls.imageId === imageId)
        impact.affectedItems.classes = affectedClasses.map((cls: any) => ({
          id: cls.id,
          name: cls.title,
          type: 'class'
        }))
      }

      // Check kits/products
      const kitsData = localStorage.getItem('salty_mermaid_kits')
      if (kitsData) {
        const kits = JSON.parse(kitsData)
        const affectedKits = kits.filter((kit: any) => kit.imageId === imageId)
        impact.affectedItems.kits = affectedKits.map((kit: any) => ({
          id: kit.id,
          name: kit.name,
          type: 'kit'
        }))
      }

      // Check gallery items
      const galleryData = localStorage.getItem('salty_mermaid_gallery_items')
      if (galleryData) {
        const galleryItems = JSON.parse(galleryData)
        const affectedGallery = galleryItems.filter((item: any) => item.imageId === imageId)
        impact.affectedItems.galleryItems = affectedGallery.map((item: any) => ({
          id: item.id,
          name: item.title,
          type: 'gallery'
        }))
      }

      impact.totalAffectedCount = 
        impact.affectedItems.classes.length + 
        impact.affectedItems.kits.length + 
        impact.affectedItems.galleryItems.length

      return impact
    } catch (error) {
      console.error('Failed to check image deletion impact:', error)
      return impact
    }
  }

  // Delete image
  deleteImage(imageId: string): boolean {
    try {
      const images = this.getAllImages()
      const imageIndex = images.findIndex(img => img.id === imageId)
      
      if (imageIndex === -1) {
        return false
      }

      const image = images[imageIndex]
      console.log(`Deleted image: ${image.name} from ${image.category} category`)
      
      // Remove from local storage
      images.splice(imageIndex, 1)
      localStorage.setItem(this.storageKey, JSON.stringify(images))
      
      this.notifySubscribers()
      return true
    } catch (error) {
      console.error('Failed to delete image:', error)
      return false
    }
  }

  // Update image metadata
  updateImage(imageId: string, updates: Partial<ImageFile>): boolean {
    try {
      const images = this.getAllImages()
      const imageIndex = images.findIndex(img => img.id === imageId)
      
      if (imageIndex === -1) {
        return false
      }

      // Update image metadata
      images[imageIndex] = { ...images[imageIndex], ...updates }
      localStorage.setItem(this.storageKey, JSON.stringify(images))
      
      this.notifySubscribers()
      return true
    } catch (error) {
      console.error('Failed to update image:', error)
      return false
    }
  }

  // Get image by ID
  getImage(imageId: string): ImageFile | null {
    const images = this.getAllImages()
    return images.find(img => img.id === imageId) || null
  }

  // Subscribe to image changes
  subscribe(callback: (images: ImageFile[]) => void): () => void {
    this.subscribers.push(callback)
    return () => {
      this.subscribers = this.subscribers.filter(sub => sub !== callback)
    }
  }

  private notifySubscribers(): void {
    const images = this.getAllImages()
    this.subscribers.forEach(callback => callback(images))
  }

  private validateImageFile(file: File): boolean {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      return false
    }

    // Check file size (5MB max)
    const maxSize = 5 * 1024 * 1024 // 5MB
    if (file.size > maxSize) {
      return false
    }

    return true
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2)
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  // Bulk operations
  deleteMultipleImages(imageIds: string[]): number {
    let deletedCount = 0
    imageIds.forEach(id => {
      if (this.deleteImage(id)) {
        deletedCount++
      }
    })
    return deletedCount
  }

  // Search images
  searchImages(query: string, category?: 'gallery' | 'classes' | 'kits'): ImageFile[] {
    let images = this.getAllImages()
    
    if (category) {
      images = images.filter(img => img.category === category)
    }

    if (query.trim() === '') {
      return images
    }

    const searchTerm = query.toLowerCase()
    return images.filter(img => 
      img.name.toLowerCase().includes(searchTerm) ||
      img.category.toLowerCase().includes(searchTerm)
    )
  }

  // Get storage stats
  getStorageStats(): { total: number, byCategory: Record<string, number> } {
    const images = this.getAllImages()
    const stats = {
      total: images.length,
      byCategory: {
        gallery: 0,
        classes: 0,
        kits: 0
      }
    }

    images.forEach(img => {
      stats.byCategory[img.category]++
    })

    return stats
  }

  // Alias for uploadImage (for compatibility)
  async addImage(file: File, category: 'gallery' | 'classes' | 'kits'): Promise<{success: boolean, imageFile?: ImageFile | null}> {
    const result = await this.uploadImage(file, category)
    return {
      success: result !== null,
      imageFile: result
    }
  }
}

export const imageManager = new ImageManagerService()
export type { ImageFile, ImageDeletionImpact, AffectedItem }